import {createStyles, Stack, Text} from '@mantine/core';
import React, {useState} from 'react';

import {pluralize} from 'BenchlingUtil';
import SelectablePlateList, {SelectablePlate} from 'samples/plate-maps/scratch-plates/SelectablePlateList';
import {PlateSize} from 'samples/storables/StorableSchemaConstants';
import legacyRunModal from 'ui-library/legacyRunModal';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import copyToClipboard from 'util/clipboard/copyToClipboard';

export interface CopyScratchPlateIdsModalProps {
  plates: Array<SelectablePlate>;
  plateSize: PlateSize;
  onRequestClose: () => void;
}

function CopyScratchPlateIdsModal({
  plates,
  plateSize,
  onRequestClose,
}: CopyScratchPlateIdsModalProps): JSX.Element {
  const {classes} = useStyles();
  if (plates.length === 0) {
    throw new Error('CopyScratchPlateIdsModal requires at least one plate.');
  }

  const [selectedPlateIds, setSelectedPlateIds] = useState<ReadonlySet<string>>(new Set());
  const selectedPlates = plates.filter((plate) => selectedPlateIds.has(plate.scratchPlateId));

  function handleToggleSelected(scratchPlateId: string): void {
    setSelectedPlateIds((prev) => {
      const next = new Set(prev);
      if (next.has(scratchPlateId)) {
        next.delete(scratchPlateId);
      } else {
        next.add(scratchPlateId);
      }
      return next;
    });
  }

  function handleSelectAll(): void {
    setSelectedPlateIds(new Set(plates.map((plate) => plate.scratchPlateId)));
  }

  function handleDeselectAll(): void {
    setSelectedPlateIds(new Set());
  }

  function handleSubmit(): void {
    copyToClipboard(selectedPlates.map((plate) => plate.scratchPlateId).join(','), {
      showToastOnSuccess: true,
      toastText: `Copied ${selectedPlates.length} plate ${pluralize(selectedPlates.length, 'ID')} to clipboard`,
    });
    onRequestClose();
  }

  const buttons: Array<ModalButton> = [
    {text: 'Cancel', cancel: true, style: 'default'},
    {text: 'Copy to clipboard', submit: true, style: 'primary', disabled: selectedPlates.length === 0},
  ];

  return (
    <Modal
      title="Copy plate IDs"
      large={true}
      onRequestClose={onRequestClose}
      onRequestSubmit={handleSubmit}
      buttons={buttons}
    >
      <Stack>
        <Text className={classes.text}>Select plates to copy their IDs.</Text>
        <SelectablePlateList
          plates={plates}
          plateSize={plateSize}
          selectedPlateIds={selectedPlateIds}
          onToggleSelected={handleToggleSelected}
          onSelectAll={handleSelectAll}
          onDeselectAll={handleDeselectAll}
        />
      </Stack>
    </Modal>
  );
}

export default CopyScratchPlateIdsModal;

export function openCopyScratchPlateIdsModal(props: {
  plates: Array<SelectablePlate>;
  plateSize: PlateSize;
}): void {
  const {plates, plateSize} = props;
  if (plates.length === 0) {
    throw new Error('openCopyScratchPlateIdsModal requires at least one plate.');
  }
  legacyRunModal(CopyScratchPlateIdsModal, {plates, plateSize});
}

const useStyles = createStyles((theme) => ({
  text: {
    fontSize: theme.fontSizes.md,
  },
}));
